import React from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, TscStyles, Glyphs, TscButtonGlyphPlacement, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import TabHeader from '../TabHeader';
import AddAssigneeModal from '../../modals/AddAssigneeModal';
import CaptionApi from '../../../service/captionApi';
import {StyledWarning, HideButtonText} from '../../util/StyledElements';
import {SetupWorkerState} from '../../../model/workerState';
import cssConstants from '../../../constants/cssConstants';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {themeStore} from '../../../context/themeProvider';
import {mediaStore, updateMediaState} from '../../../context/mediaProvider';
import {AlertModalPortal} from '../../util/ModalPortalHelper';

const ButtonGroup = styled.div`
   display: flex;

   div:first-of-type button {
      margin-right: 0.5rem;
   }

   @media screen and (max-width: ${cssConstants.tableBreakpoint(false)}px){
      width: auto;
   }
`;

const Container = styled.div<{theme: TscThemeNames}>`
   padding-bottom: 1rem;
   border-bottom: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium};
`;

export const AssigneeBase: React.FC<IStateMappedProps> = ({theme, MediaId, CaptioningAssignedToEmail}) => {
   const errorEl = React.useRef<HTMLDivElement>(null);
   const {t} = useTranslation();
   const [isModalDisplayed, setIsModalDisplayed] = React.useState(false);
   const [isAlertDisplayed, setAlertDisplayed] = React.useState(false);
   const [workerState, setWorkerState] = SetupWorkerState();

   const onToggleModal = () => {
      setIsModalDisplayed(!isModalDisplayed);
   };

   const onToggleAlert = () => {
      setAlertDisplayed(!isAlertDisplayed);
   };

   const removeEmail = async () => {
      setWorkerState({isWorking: true, hasError: false});
      setAlertDisplayed(false);

      try {
         await CaptionApi.removeAssignee(MediaId);
         updateMediaState({CaptioningAssignedToEmail: ''});
         setWorkerState({isWorking: false, hasError: false});
      } catch (ex) {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   const getLabel = () => {
      if (workerState.isWorking && CaptioningAssignedToEmail) {
         return t('accessibility.assignee.deleting');
      }

      if (CaptioningAssignedToEmail) {
         return CaptioningAssignedToEmail;
      }

      return t('accessibility.assignee.none');
   };

   const addButton =
      (<HideButtonText breakpointWidth={cssConstants.accessibilityTabButtonBreakpoint} glyphPlacement={TscButtonGlyphPlacement.left}>
         <ControlButton
            label={t('accessibility.assignee.addAssignee')}
            buttonType={TscButtonType.secondary}
            themeName={theme}
            onClick={onToggleModal}
            glyph={<Glyphs.Add16x16 />}
            glyphPlacement={TscButtonGlyphPlacement.left}
            disabled={workerState.isWorking}
            width="13.5rem"
            testId="assignee-add-button"
         />
      </HideButtonText>)
   ;

   const modifyButton =
      (<HideButtonText breakpointWidth={cssConstants.accessibilityTabButtonBreakpoint} glyphPlacement={TscButtonGlyphPlacement.left}>
         <ControlButton
            label={t('accessibility.modify')}
            buttonType={TscButtonType.secondary}
            themeName={theme}
            onClick={onToggleModal}
            glyph={<Glyphs.Edit16x16 />}
            glyphPlacement={TscButtonGlyphPlacement.left}
            disabled={workerState.isWorking}
            width="6.5rem"
            testId="assignee-modify-button"
         />
      </HideButtonText>)
   ;

   const removeButton =
      (<HideButtonText breakpointWidth={cssConstants.accessibilityTabButtonBreakpoint} glyphPlacement={TscButtonGlyphPlacement.left}>
         <ControlButton
            label={t('accessibility.remove')}
            buttonType={TscButtonType.secondary}
            themeName={theme}
            onClick={onToggleAlert}
            glyph={<Glyphs.CircleRemove16x16 />}
            glyphPlacement={TscButtonGlyphPlacement.left}
            disabled={workerState.isWorking}
            width="6.5rem"
            testId="assignee-remove-button"
         />
      </HideButtonText>)
   ;

   const modifyRemoveButtonGroup = <ButtonGroup>{modifyButton}{removeButton}</ButtonGroup>;

   const removeAssigneeAlertModal: React.JSX.Element =
      (<AlertModalPortal
         title={t('accessibility.assignee.alert.title')}
         onClose={onToggleAlert}
         onConfirm={removeEmail}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t('accessibility.remove')}
         onDismiss={onToggleAlert}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.cancel')}
      >{t('accessibility.assignee.alert.text', {assignee: CaptioningAssignedToEmail})}</AlertModalPortal>);

   return (
      <Container theme={theme}>
         <TabHeader leftLabel={t('accessibility.assignee.label')} rightLabel={getLabel()} hideRightLabelInMobileView={false} button={CaptioningAssignedToEmail ? modifyRemoveButtonGroup : addButton} />
         {workerState.hasError && <StyledWarning id="error-message-remove" ref={errorEl}>{t('accessibility.assignee.removeError')}</StyledWarning>}
         {isModalDisplayed && <AddAssigneeModal currentEmail={CaptioningAssignedToEmail} mediaId={MediaId} onClose={onToggleModal} />}
         {isAlertDisplayed && removeAssigneeAlertModal}
      </Container>
   );
};

export interface IStateMappedProps {
   theme: TscThemeNames;
   MediaId: string;
   CaptioningAssignedToEmail: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {theme}: Partial<IStateMappedProps>,
   {MediaId, CaptioningAssignedToEmail}: Partial<IStateMappedProps>
): IStateMappedProps => ({theme, MediaId, CaptioningAssignedToEmail});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(AssigneeBase);
